import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Plus, Scissors, Trash2, Check, Loader2, Users as UsersIcon } from 'lucide-react';
import PrimaryButton from '../ui/PrimaryButton';
import { useEvents } from '@/contexts/EventsContext';
import { useUsers } from '@/contexts/UsersContext';
import { toast } from 'sonner';
import ConfirmDialog from '../ui/ConfirmDialog';
import NewServiceModal from './NewServiceModal';
import { useTranslation } from 'react-i18next';

interface SalonServicesProps {
  organizationId: string;
}

interface ServiceGroup {
  key: string;
  name: string;
  category_id: number;
  categoryName: string;
  price: number; // prezzo di riferimento (usato come base per nuove assegnazioni)
  duration: number;
  description: string;
  byStaff: Map<string, { id: string; price: number }>; // hairdresser_id -> { performance id, prezzo del dipendente }
}

const SalonServices: React.FC<SalonServicesProps> = ({ organizationId }) => {
  const { t } = useTranslation();
  const { users } = useUsers();
  const { categories, shopCategories, performances, deletePerformance, insertPerformance, updatePerformance } = useEvents();

  const [activeCategoryName, setActiveCategoryName] = useState('Tutti');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<ServiceGroup | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  // Prezzo per-dipendente in modifica (keyed by performance id) e stato di salvataggio
  const [priceEdits, setPriceEdits] = useState<Record<string, string>>({});
  const [priceSaving, setPriceSaving] = useState<Record<string, boolean>>({});
  // Durata del servizio in modifica (keyed by service key): applicata a tutte le Performances del gruppo
  const [durationEdits, setDurationEdits] = useState<Record<string, string>>({});
  const [durationSaving, setDurationSaving] = useState<Record<string, boolean>>({});

  // Salva il prezzo di una specifica Performance (prezzo del servizio per quel dipendente).
  const savePrice = async (perfId: string, currentPrice: number) => {
    const raw = priceEdits[perfId];
    if (raw === undefined) return; // non modificato
    const parsed = Math.round(Number(raw));
    setPriceEdits((p) => { const n = { ...p }; delete n[perfId]; return n; });
    if (isNaN(parsed) || parsed < 0 || parsed === Math.round(currentPrice)) return; // invariato o non valido
    setPriceSaving((s) => ({ ...s, [perfId]: true }));
    try {
      // Performances.id è un UUID: si passa così com'è (come deletePerformance), non convertito a number.
      const res = await updatePerformance(perfId as any, { price: parsed });
      if (res && res.success === false) throw new Error(res.error);
      toast.success(t('salon.services.toast.price_updated', 'Prezzo aggiornato'));
    } catch (error: any) {
      toast.error(error?.message || t('salon.services.toast.update_error', 'Errore aggiornamento servizio'));
    } finally {
      setPriceSaving((s) => ({ ...s, [perfId]: false }));
    }
  };

  const activeStaff = useMemo(() => users.filter((u: any) => u.is_active !== false), [users]);

  const categoryIdToName = useMemo(() => {
    const map: Record<number, string> = {};
    categories.forEach((c: any) => (map[c.id] = c.name));
    return map;
  }, [categories]);

  const categoriesList = useMemo(() => {
    if (!shopCategories) return ['Tutti'];
    // Nomi delle categorie offerte dal salone: rimuovo id non risolti e duplicati.
    const names = shopCategories
      .map((c: any) => categoryIdToName[c?.category_id])
      .filter((name: string | undefined): name is string => !!name);
    return ['Tutti', ...Array.from(new Set(names))];
  }, [shopCategories, categoryIdToName]);

  // Raggruppa le Performance in "servizi" (per nome + categoria); ogni servizio è offerto da 0..N dipendenti.
  const services = useMemo<ServiceGroup[]>(() => {
    const map = new Map<string, ServiceGroup>();
    (performances || [])
      .filter((p: any) => p.is_active !== false)
      .forEach((p: any) => {
        const key = `${(p.name || '').trim().toLowerCase()}||${p.category_id}`;
        if (!map.has(key)) {
          map.set(key, {
            key,
            name: p.name || t('common.unknown'),
            category_id: p.category_id,
            categoryName: categoryIdToName[p.category_id],
            price: p.price,
            duration: p.duration,
            description: p.description,
            byStaff: new Map(),
          });
        }
        map.get(key)!.byStaff.set(p.hairdresser_id, { id: p.id, price: p.price });
      });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [performances, categoryIdToName, t]);

  const displayedServices = useMemo(
    () => services.filter((s) => activeCategoryName === 'Tutti' || s.categoryName === activeCategoryName),
    [services, activeCategoryName],
  );

  // Salva la durata del servizio, retroattivamente su tutte le Performances del gruppo (tutti i dipendenti).
  const saveDuration = async (service: ServiceGroup) => {
    const raw = durationEdits[service.key];
    if (raw === undefined) return; // non modificata
    const parsed = Math.round(Number(raw));
    setDurationEdits((p) => { const n = { ...p }; delete n[service.key]; return n; });
    if (isNaN(parsed) || parsed <= 0 || parsed === Math.round(service.duration)) return;
    setDurationSaving((s) => ({ ...s, [service.key]: true }));
    try {
      for (const perf of Array.from(service.byStaff.values()) as Array<{ id: string; price: number }>) {
        const res = await updatePerformance(perf.id as any, { duration: parsed });
        if (res && res.success === false) throw new Error(res.error);
      }
      toast.success(t('salon.services.toast.duration_updated', 'Durata aggiornata per tutti i dipendenti'));
    } catch (error: any) {
      toast.error(error?.message || t('salon.services.toast.update_error', 'Errore aggiornamento servizio'));
    } finally {
      setDurationSaving((s) => ({ ...s, [service.key]: false }));
    }
  };

  // Assegna/rimuove un dipendente da un servizio (crea/elimina la Performance).
  const toggleStaff = async (service: ServiceGroup, staffId: string) => {
    const bkey = `${service.key}:${staffId}`;
    if (busy[bkey]) return;
    setBusy((b) => ({ ...b, [bkey]: true }));
    try {
      const existing = service.byStaff.get(staffId);
      if (existing) {
        await deletePerformance(existing.id);
      } else {
        const res = await insertPerformance({
          shop_id: organizationId,
          name: service.name,
          description: service.description || '',
          category_id: service.category_id,
          price: service.price,
          duration: service.duration,
          hairdresser_id: staffId,
        });
        if (res && res.success === false) throw new Error(res.error);
      }
    } catch (error: any) {
      toast.error(error?.message || t('salon.services.toast.update_error', 'Errore aggiornamento servizio'));
    } finally {
      setBusy((b) => ({ ...b, [bkey]: false }));
    }
  };

  // Elimina l'intero servizio (tutte le Performance dei dipendenti).
  const handleConfirmDelete = async () => {
    if (!serviceToDelete) return;
    setIsDeleting(true);
    try {
      for (const perf of Array.from(serviceToDelete.byStaff.values()) as Array<{ id: string; price: number }>) {
        await deletePerformance(perf.id);
      }
      toast.success(t('salon.services.toast.delete_success'));
      setServiceToDelete(null);
    } catch (error) {
      toast.error(t('salon.services.toast.delete_error'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <motion.div
      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
      className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6"
    >
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        {/* Categorie */}
        <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto custom-scrollbar order-2 md:order-1">
          {categoriesList.map((cat: string) => (
            <button
              key={cat}
              onClick={() => setActiveCategoryName(cat)}
              className={`px-4 py-1.5 rounded-full border text-sm font-medium transition-colors whitespace-nowrap ${activeCategoryName === cat ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              {cat === 'Tutti' ? t('salon.services.filters.all') : cat}
            </button>
          ))}
        </div>

        <PrimaryButton
          label={t('common.new')}
          icon={Plus}
          onClick={() => setIsModalOpen(true)}
          className="bg-gray-900 hover:bg-black cursor-pointer order-1 md:order-2 w-full md:w-auto justify-center"
        />
      </div>

      <div className="grid grid-cols-1 gap-3">
        {displayedServices.length === 0 && (
          <div className="text-center py-12 border-2 border-dashed border-gray-100 rounded-2xl">
            <Scissors size={24} className="mx-auto text-gray-300 mb-2" />
            <p className="text-gray-400 text-sm">{t('salon.services.empty_state')}</p>
          </div>
        )}

        {displayedServices.map((service) => {
          const prices = (Array.from(service.byStaff.values()) as Array<{ id: string; price: number }>).map((v) => Math.round(Number(v.price)));
          const minPrice = prices.length ? Math.min(...prices) : Math.round(service.price);
          const priceVaries = new Set(prices).size > 1;
          return (
          <div key={service.key} className="p-4 border border-gray-100 rounded-2xl bg-white space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-12 h-12 shrink-0 bg-clip-50 rounded-xl flex items-center justify-center text-clip-500 shadow-sm">
                  <Scissors size={18} />
                </div>
                <div className="min-w-0">
                  <h4 className="font-bold text-gray-900 text-sm truncate">{service.name}</h4>
                  {service.categoryName && (
                    <span className="text-[10px] uppercase font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-md mt-1 inline-block">
                      {service.categoryName}
                    </span>
                  )}
                  {service.description && <p className="text-xs text-gray-400 mt-1 truncate">{service.description}</p>}
                </div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <div className="text-right">
                  <span className="block font-bold text-gray-900">{priceVaries ? `da € ${minPrice}` : `€ ${minPrice}`}</span>
                  <div className="relative flex items-center justify-end gap-1 mt-0.5">
                    <input
                      type="number"
                      min="5"
                      step="5"
                      inputMode="numeric"
                      title={t('salon.services.duration_hint', 'Durata del servizio (si applica a tutti i dipendenti)')}
                      value={durationEdits[service.key] ?? String(Math.round(service.duration))}
                      onChange={(e) => setDurationEdits((p) => ({ ...p, [service.key]: e.target.value }))}
                      onBlur={() => saveDuration(service)}
                      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                      disabled={durationSaving[service.key]}
                      className="w-14 px-1.5 py-0.5 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-700 text-right focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none disabled:opacity-60"
                    />
                    <span className="text-xs text-gray-400">min</span>
                    {durationSaving[service.key] && <Loader2 size={12} className="animate-spin text-clip-500" />}
                  </div>
                </div>
                <button
                  onClick={() => setServiceToDelete(service)}
                  title={t('salon.services.delete.title')}
                  className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors cursor-pointer"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>

            {/* Assegnazione dipendenti via checkbox */}
            <div className="pt-3 border-t border-gray-50">
              <div className="flex items-center gap-1.5 text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                <UsersIcon size={13} /> Chi lo effettua
                <span className="normal-case tracking-normal text-gray-300 font-medium">· prezzo per dipendente</span>
              </div>
              <div className="space-y-2">
                {activeStaff.length === 0 && <p className="text-xs text-gray-400">Nessun dipendente attivo.</p>}
                {activeStaff.map((u: any) => {
                  const info = service.byStaff.get(u.id);
                  const checked = !!info;
                  const perfId = info?.id;
                  const bkey = `${service.key}:${u.id}`;
                  const isBusy = busy[bkey];
                  const priceValue = perfId !== undefined
                    ? (priceEdits[perfId] ?? String(Math.round(Number(info!.price))))
                    : '';
                  return (
                    <div key={u.id} className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => toggleStaff(service, u.id)}
                        disabled={isBusy}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-bold transition-colors disabled:opacity-60 ${checked ? 'bg-clip-50 border-clip-300 text-clip-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                      >
                        <span className={`w-4 h-4 rounded flex items-center justify-center border ${checked ? 'bg-clip-500 border-clip-500 text-white' : 'border-gray-300 bg-white'}`}>
                          {isBusy ? <Loader2 size={11} className="animate-spin text-clip-500" /> : checked ? <Check size={12} /> : null}
                        </span>
                        {u.first_name} {u.last_name}
                      </button>

                      {/* Prezzo del servizio per questo dipendente (solo se assegnato) */}
                      {checked && perfId !== undefined && (
                        <div className="relative flex items-center shrink-0">
                          <span className="absolute left-2.5 text-xs font-bold text-gray-400 pointer-events-none">€</span>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            inputMode="numeric"
                            value={priceValue}
                            onChange={(e) => setPriceEdits((p) => ({ ...p, [perfId]: e.target.value }))}
                            onBlur={() => savePrice(perfId, Number(info!.price))}
                            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                            disabled={priceSaving[perfId]}
                            className="w-24 pl-6 pr-7 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-900 text-right focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none disabled:opacity-60"
                          />
                          {priceSaving[perfId] && <Loader2 size={12} className="absolute right-2 animate-spin text-clip-500" />}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          );
        })}
      </div>

      <NewServiceModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} organizationId={organizationId} />

      <ConfirmDialog
        isOpen={!!serviceToDelete}
        title={t('salon.services.delete.title')}
        description={t('salon.services.delete.description', { name: serviceToDelete?.name })}
        confirmLabel={t('salon.services.delete.confirm')}
        onConfirm={handleConfirmDelete}
        onCancel={() => setServiceToDelete(null)}
        loading={isDeleting}
      />
    </motion.div>
  );
};

export default SalonServices;
